import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaUser } from 'react-icons/fa';
import ApiClient from '../../methods/api/apiClient';

// Public, tokenized ConnectMeet (CM) scheduling preference form.
// Opened from the grouping email ("Schedule my {label}") via ?session=... or ?token=...
// Works logged-out, like RsvpConfirmation.

// Hourly slots from 7 AM to 8 PM (last slot 7–8 PM). The matcher intersects hourly
// buckets, so 1-hour slots need no backend change.
const GRID_START_HOUR = 7;   // 7 AM
const GRID_END_HOUR = 20;    // 8 PM (end of the last slot)
const hourLabel = (h: number) => {
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12} ${ampm}`;
};
const TIME_BLOCKS = (() => {
    const blocks: { label: string; startHour: number; endHour: number }[] = [];
    for (let h = GRID_START_HOUR; h < GRID_END_HOUR; h++) {
        blocks.push({ label: hourLabel(h), startHour: h, endHour: h + 1 });
    }
    return blocks;
})();

const ACTIVITIES = ['Breakfast', 'Coffee', 'Lunch', 'Other'];

// Absolute epoch-ms of a slot's start (prefers startMs; legacy fallback = UTC date + hour).
function slotStartMs(s: any): number | null {
    if (s && typeof s.startMs === 'number') return s.startMs;
    if (s && s.date && typeof s.startHour === 'number') {
        const d = new Date(s.date);
        return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), s.startHour);
    }
    return null;
}
// Map an absolute instant to a grid cell in the VIEWER'S local time. null if outside the grid hours.
function msToCell(ms: number): { dayKey: string; blockIdx: number } | null {
    const d = new Date(ms);
    const blockIdx = d.getHours() - GRID_START_HOUR;
    if (blockIdx < 0 || blockIdx >= TIME_BLOCKS.length) return null;
    return { dayKey: `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`, blockIdx };
}

function nextDays(n: number) {
    const out: { y: number; m: number; d: number; label: string; key: string }[] = [];
    const now = new Date();
    for (let i = 0; i < n; i++) {
        const dt = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i);
        const key = `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
        out.push({
            y: dt.getFullYear(), m: dt.getMonth(), d: dt.getDate(), key,
            label: dt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
        });
    }
    return out;
}

const CmSchedule: React.FC = () => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    const session = params.get('session') || '';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState<any>(null);

    const [meEmail, setMeEmail] = useState('');
    // Multi-select meeting mode: the user may pick in-person AND/OR virtual (at least one).
    const [meetingModes, setMeetingModes] = useState<string[]>(['in-person']);
    const [activity, setActivity] = useState('Coffee');
    const [activityOther, setActivityOther] = useState('');
    const [videoLink, setVideoLink] = useState('');
    // Selected places from the place picker (multi-select): [{name,address}].
    const [selectedPlaces, setSelectedPlaces] = useState<{ name: string; address: string }[]>([]);
    // selected: key -> set of block indexes (MY availability, editable)
    const [selected, setSelected] = useState<Record<string, Set<number>>>({});
    // Name-filter for the heatmap: emails toggled ON in the right panel. Empty = show the whole group.
    const [activePeople, setActivePeople] = useState<Set<string>>(new Set());
    const togglePerson = (email: string) => setActivePeople(prev => {
        // Build without passing an iterable to the Set constructor (tsconfig target es5 -> TS2802).
        const next = new Set<string>();
        Array.from(prev).forEach(e => { if (e !== email) next.add(e); });
        if (!prev.has(email)) next.add(email);
        return next;
    });

    // Calendar free/busy overlay (spec §8). Optional: works only when the person is signed in
    // and has connected a calendar under ConnectMeet Preferences.
    const [useCalendar, setUseCalendar] = useState(false);
    const [calLoading, setCalLoading] = useState(false);
    const [calNote, setCalNote] = useState('');
    const [calShowConnect, setCalShowConnect] = useState(false);
    // busy intervals as epoch-ms ranges
    const [busyMs, setBusyMs] = useState<{ start: number; end: number }[]>([]);

    // When the form is opened via a per-member token (or a session link that resolves "me"),
    // the participant is known — auto-fill meEmail and hide the "which participant are you?" picker.
    const [meLocked, setMeLocked] = useState(false);

    // Schej/when2meet-style drag-to-paint state. Refs (not state) so the document-level
    // mouseup/touchend handlers and rapid pointer moves always read the live values.
    const [dragging, setDragging] = useState(false);
    const draggingRef = useRef(false);
    const dragModeRef = useRef<'select' | 'deselect'>('select');
    const gridRef = useRef<HTMLDivElement>(null);

    const days = nextDays(28);

    // Does a given day+time-block overlap a busy interval? Blocks are the participant's LOCAL time,
    // busy intervals are real instants (UTC) from their calendar, so we compare real instants.
    const blockIsBusy = (day: { y: number; m: number; d: number }, blockIdx: number) => {
        if (!useCalendar || !busyMs.length) return false;
        const b = TIME_BLOCKS[blockIdx];
        const bStart = new Date(day.y, day.m, day.d, b.startHour).getTime();
        const bEnd = new Date(day.y, day.m, day.d, b.endHour).getTime();
        return busyMs.some(iv => iv.start < bEnd && iv.end > bStart);
    };

    const toggleUseCalendar = () => {
        if (useCalendar) { setUseCalendar(false); setCalNote(''); return; }
        setCalLoading(true);
        setCalNote('');
        setCalShowConnect(false);
        const now = new Date();
        const end = new Date(now.getTime() + 28 * 24 * 60 * 60 * 1000);
        ApiClient.post('api/calendar/freebusy', { start: now.toISOString(), end: end.toISOString() })
            .then((res: any) => {
                setCalLoading(false);
                if (res.success) {
                    if (!res.data.connected || !res.data.connected.length) {
                        setCalNote('No calendar connected. Connect your account here:');
                        setCalShowConnect(true);
                        return;
                    }
                    const ivs = (res.data.busy || []).map((b: any) => ({ start: new Date(b.start).getTime(), end: new Date(b.end).getTime() }));
                    setBusyMs(ivs);
                    setUseCalendar(true);
                    // cw: cells another member's CALENDAR is busy in — the fill skips these, since that
                    // person already can't make it ("skip 8 PM since A can't make it"). Based on BUSY
                    // slots only, so someone's manual picks never restrict what you can offer.
                    const othersBusy: Record<string, boolean> = {};
                    (form?.memberSlots || []).forEach((m: any) => {
                        if (m.isMe) return;
                        (m.busySlots || []).forEach((s: any) => {
                            const st = slotStartMs(s);
                            if (st == null) return;
                            const cell = msToCell(st);
                            if (cell) othersBusy[`${cell.dayKey}:${cell.blockIdx}`] = true;
                        });
                    });
                    // cw: fill in availability — mark every block I'm free in as available, EXCEPT slots
                    // another member's calendar is busy in. The member can then deselect any remaining
                    // times they don't actually want.
                    setSelected(() => {
                        const next: Record<string, Set<number>> = {};
                        for (const day of days) {
                            const set = new Set<number>();
                            TIME_BLOCKS.forEach((bl, idx) => {
                                const bStart = new Date(day.y, day.m, day.d, bl.startHour).getTime();
                                const bEnd = new Date(day.y, day.m, day.d, bl.endHour).getTime();
                                const busy = ivs.some((iv: { start: number; end: number }) => iv.start < bEnd && iv.end > bStart);
                                if (busy) return;
                                if (othersBusy[`${day.key}:${idx}`]) return; // another member can't make it
                                set.add(idx);
                            });
                            if (set.size) next[day.key] = set;
                        }
                        return next;
                    });
                } else {
                    setCalNote(res.message === 'Authentication required'
                        ? 'Please sign in to use your calendar. You can still pick times manually.'
                        : (res.message || 'Could not read your calendar.'));
                }
            })
            .catch(() => { setCalLoading(false); setCalNote('Could not read your calendar. You can still pick times manually.'); });
    };

    useEffect(() => {
        const q: any = token ? { token } : { session };
        ApiClient.get('api/cm/schedule/form', q).then((res: any) => {
            setLoading(false);
            if (res.success) {
                setForm(res.data);
                if (res.data.me) { setMeEmail(res.data.me.email); setMeLocked(true); }
            } else {
                setError(res.message || 'This scheduler link is invalid or expired.');
            }
        }).catch(() => { setLoading(false); setError('This scheduler link is invalid or expired.'); });
    }, [token, session]);

    // Token-less (?session=) link: the initial load can't know which member the viewer is, so their
    // saved favorite places come back empty. Once they pick their name (meEmail), re-resolve just
    // the preset/others places by email — without disturbing the grid selections already made.
    useEffect(() => {
        if (token || !meEmail || !session) return;
        ApiClient.get('api/cm/schedule/form', { session, email: meEmail }).then((res: any) => {
            if (res.success && res.data) {
                setForm((prev: any) => ({
                    ...prev,
                    presetPlaces: res.data.presetPlaces || [],
                    othersPlaces: res.data.othersPlaces || prev?.othersPlaces || [],
                    mySlots: res.data.mySlots || [],
                    memberSlots: res.data.memberSlots || prev?.memberSlots || [],
                }));
            }
        }).catch(() => { /* non-fatal: manual place selection still works */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meEmail, token, session]);

    // Prefill the grid with THIS member's previously-submitted availability (once), so reopening
    // the form to "update availability" shows what they picked before instead of a blank grid.
    const prefilledRef = useRef(false);
    useEffect(() => {
        if (prefilledRef.current) return;
        const mine = form?.mySlots;
        if (!Array.isArray(mine) || !mine.length) return;
        const next: Record<string, Set<number>> = {};
        mine.forEach((s: any) => {
            const start = slotStartMs(s);
            if (start == null) return;
            const cell = msToCell(start);
            if (!cell) return;
            (next[cell.dayKey] = next[cell.dayKey] || new Set<number>()).add(cell.blockIdx);
        });
        if (Object.keys(next).length) { setSelected(next); prefilledRef.current = true; }
    }, [form]);

    // Group availability heatmap (Schej-style): per grid cell, how many of the ACTIVE people are
    // free (darker orange = more). `activePeople` filters via the name toggles on the right (empty
    // = whole group). "Me" uses the live `selected` grid so my edits show instantly. `size` is the
    // number of people counted, used to scale the 3 orange shades.
    const heat = useMemo(() => {
        const count: Record<string, number> = {};
        const names: Record<string, string[]> = {};
        const busy: Record<string, number> = {};
        const all = form?.memberSlots || [];
        const members = activePeople.size ? all.filter((m: any) => activePeople.has(m.email)) : all;
        // cw: track AVAILABLE (selected) and BUSY (calendar) separately. A slot is only red when a
        // member's CALENDAR marks them busy there — never just because someone didn't select it.
        // Denominator = people who responded (have availability or busy from a submission).
        let submittedCount = 0;
        members.forEach((mem: any) => {
            const availKeys: string[] = [];
            const busyKeys: string[] = [];
            if (mem.isMe) {
                Object.keys(selected).forEach(dk => Array.from(selected[dk] || []).forEach(bi => availKeys.push(`${dk}:${bi}`)));
                // my own live calendar-busy is drawn as hatched separately; don't count it here.
            } else {
                (mem.slots || []).forEach((s: any) => {
                    const start = slotStartMs(s);
                    if (start == null) return;
                    const cell = msToCell(start);
                    if (cell) availKeys.push(`${cell.dayKey}:${cell.blockIdx}`);
                });
                (mem.busySlots || []).forEach((s: any) => {
                    const start = slotStartMs(s);
                    if (start == null) return;
                    const cell = msToCell(start);
                    if (cell) busyKeys.push(`${cell.dayKey}:${cell.blockIdx}`);
                });
            }
            if (availKeys.length || busyKeys.length) submittedCount++;
            availKeys.forEach(key => {
                count[key] = (count[key] || 0) + 1;
                (names[key] = names[key] || []).push(mem.isMe ? 'You' : (mem.name || mem.email));
            });
            busyKeys.forEach(key => { busy[key] = (busy[key] || 0) + 1; });
        });
        return { count, names, busy, size: submittedCount };
    }, [form, activePeople, selected]);

    const toggleBlock = (dayKey: string, blockIdx: number, busy?: boolean) => {
        if (busy) return; // busy blocks are non-selectable when the calendar overlay is on
        setSelected(prev => {
            const next = { ...prev };
            const set = new Set(next[dayKey] || []);
            if (set.has(blockIdx)) set.delete(blockIdx); else set.add(blockIdx);
            next[dayKey] = set;
            return next;
        });
    };

    // ── drag-to-paint (Schej / when2meet) ──
    // paintCell forces a cell to the given mode ('select' adds, 'deselect' removes) — used by the
    // drag so re-entering a cell mid-drag is idempotent rather than toggling it back and forth.
    const paintCell = (dayKey: string, blockIdx: number, mode: 'select' | 'deselect') => {
        setSelected(prev => {
            const next = { ...prev };
            const set = new Set(next[dayKey] || []);
            if (mode === 'select') set.add(blockIdx); else set.delete(blockIdx);
            next[dayKey] = set;
            return next;
        });
    };

    // The first cell touched decides the mode for the whole drag: an empty cell → SELECT, a filled
    // cell → DESELECT. A plain click (mousedown+mouseup on one cell) therefore toggles that cell.
    const startPaint = (dayKey: string, blockIdx: number, busy: boolean) => {
        if (busy) return;
        const isOn = !!(selected[dayKey] && selected[dayKey].has(blockIdx));
        const mode: 'select' | 'deselect' = isOn ? 'deselect' : 'select';
        dragModeRef.current = mode;
        draggingRef.current = true;
        setDragging(true);
        paintCell(dayKey, blockIdx, mode);
    };

    const enterPaint = (dayKey: string, blockIdx: number, busy: boolean) => {
        if (!draggingRef.current || busy) return;
        paintCell(dayKey, blockIdx, dragModeRef.current);
    };

    const endPaint = () => {
        if (!draggingRef.current) return;
        draggingRef.current = false;
        setDragging(false);
    };

    const clearAll = () => setSelected({});

    const selectAll = () => {
        const next: Record<string, Set<number>> = {};
        for (const day of days) {
            const set = new Set<number>();
            for (let idx = 0; idx < TIME_BLOCKS.length; idx++) {
                if (!blockIsBusy(day, idx)) set.add(idx);
            }
            if (set.size) next[day.key] = set;
        }
        setSelected(next);
    };

    // End the drag even if the pointer is released outside the grid.
    useEffect(() => {
        const up = () => endPaint();
        document.addEventListener('mouseup', up);
        document.addEventListener('touchend', up);
        document.addEventListener('touchcancel', up);
        return () => {
            document.removeEventListener('mouseup', up);
            document.removeEventListener('touchend', up);
            document.removeEventListener('touchcancel', up);
        };
    }, []);

    // Touch painting: React's onTouchMove fires on the START element, so track the finger with
    // elementFromPoint over the cells' data-* attributes. Attach natively with { passive: false }
    // so we can preventDefault (block the page from scrolling) while a paint drag is in progress.
    useEffect(() => {
        const el = gridRef.current;
        if (!el) return;
        const onMove = (e: TouchEvent) => {
            if (!draggingRef.current) return;
            e.preventDefault();
            const t = e.touches[0];
            if (!t) return;
            const target = document.elementFromPoint(t.clientX, t.clientY) as HTMLElement | null;
            if (!target) return;
            const dayKey = target.getAttribute('data-daykey');
            const blockAttr = target.getAttribute('data-block');
            if (dayKey === null || blockAttr === null) return;
            const busy = target.getAttribute('data-busy') === '1';
            enterPaint(dayKey, parseInt(blockAttr, 10), busy);
        };
        el.addEventListener('touchmove', onMove, { passive: false });
        return () => el.removeEventListener('touchmove', onMove);
    }, [selected, useCalendar, busyMs]);

    // ── meeting mode (multi-select) ──
    const toggleMode = (m: string) => {
        setMeetingModes(prev => prev.indexOf(m) !== -1 ? prev.filter(x => x !== m) : prev.concat([m]));
    };
    const modeOn = (m: string) => meetingModes.indexOf(m) !== -1;

    // ── place picker (multi-select) ──
    const placeKey = (p: { name: string; address: string }) => `${p.name}||${p.address || ''}`;
    const placeOn = (p: { name: string; address: string }) => selectedPlaces.some(sp => placeKey(sp) === placeKey(p));
    const togglePlace = (p: { name: string; address: string }) => {
        setSelectedPlaces(prev => prev.some(sp => placeKey(sp) === placeKey(p))
            ? prev.filter(sp => placeKey(sp) !== placeKey(p))
            : prev.concat([{ name: p.name, address: p.address || '' }]));
    };

    // The user's saved favorite places for the CHOSEN activity (presetPlaces is grouped by activity).
    const myPlaces: { name: string; address: string }[] = (form?.presetPlaces || [])
        .filter((fp: any) => fp && fp.activity === activity)
        .flatMap((fp: any) => (fp.places || []).map((pl: any) => ({ name: pl.name, address: pl.address || '' })))
        .filter((pl: any) => pl && pl.name);

    // Places other members of this session already selected (deduped against my own favorites).
    const myPlaceKeys: Record<string, boolean> = {};
    myPlaces.forEach(pl => { myPlaceKeys[placeKey(pl)] = true; });
    const othersPlaces: { name: string; address: string }[] = (form?.othersPlaces || [])
        .map((pl: any) => ({ name: pl.name, address: pl.address || '' }))
        .filter((pl: any) => pl && pl.name && !myPlaceKeys[placeKey(pl)]);

    const buildSlots = () => {
        const slots: any[] = [];
        for (const day of days) {
            const set = selected[day.key];
            if (!set || !set.size) continue;
            // Array.from so we don't iterate a Set directly (tsconfig target es5 -> TS2802).
            for (const idx of Array.from(set)) {
                const b = TIME_BLOCKS[idx];
                // Convert the picked LOCAL date+hour to an absolute instant (epoch ms). `new Date(y,
                // m,d,hour)` is interpreted in the VIEWER'S timezone, so 7 AM PST and 7 AM EST become
                // different instants — only genuinely-overlapping times match across timezones.
                const startLocal = new Date(day.y, day.m, day.d, b.startHour);
                const endLocal = new Date(day.y, day.m, day.d, b.endHour);
                slots.push({
                    startMs: startLocal.getTime(),
                    endMs: endLocal.getTime(),
                    // Legacy fields (UTC-midnight date + naive hours) kept for back-compat readers.
                    date: new Date(Date.UTC(day.y, day.m, day.d)).toISOString(),
                    startHour: b.startHour,
                    endHour: b.endHour,
                });
            }
        }
        return slots;
    };

    // cw: calendar-detected busy cells (only when the member used "fill in my availability").
    // Stored so others see those slots as red — an explicit "can't make it".
    const buildBusySlots = () => {
        if (!useCalendar) return [];
        const slots: any[] = [];
        for (const day of days) {
            TIME_BLOCKS.forEach((b, idx) => {
                if (!blockIsBusy(day, idx)) return;
                const startLocal = new Date(day.y, day.m, day.d, b.startHour);
                const endLocal = new Date(day.y, day.m, day.d, b.endHour);
                slots.push({
                    startMs: startLocal.getTime(),
                    endMs: endLocal.getTime(),
                    date: new Date(Date.UTC(day.y, day.m, day.d)).toISOString(),
                    startHour: b.startHour,
                    endHour: b.endHour,
                });
            });
        }
        return slots;
    };

    const submit = () => {
        if (submitting) return; // guard double-tap (PWA fires onClick twice) / double-click
        const slots = buildSlots();
        if (!meEmail) { setError('Please select which participant you are.'); return; }
        if (!meetingModes.length) { setError('Please pick at least one way to meet (in-person or virtual).'); return; }
        if (!slots.length) { setError('Please pick at least one available time.'); return; }
        setError('');
        setSubmitting(true);
        const wantsInPerson = modeOn('in-person');
        const wantsVirtual = modeOn('virtual');
        const places = wantsInPerson ? selectedPlaces : [];
        const payload: any = {
            meetingModes,
            meetingMode: meetingModes[0], // back-compat: first selected
            activity, activityOther,
            availableSlots: slots,
            busySlots: buildBusySlots(),
            // The submitter's IANA timezone, so their availability + the resolved time can be shown
            // in their own local time (and for reference when displaying to others).
            timezone: (typeof Intl !== 'undefined' && Intl.DateTimeFormat)
                ? (Intl.DateTimeFormat().resolvedOptions().timeZone || '') : '',
            videoLink: wantsVirtual ? videoLink : '',
            // Multi-select place picker; back-compat locationChoice is set server-side from the first.
            locationChoices: places.map(p => ({ name: p.name, address: p.address || '' })),
            locationChoice: (wantsInPerson && places.length)
                ? { type: 'preset', presetName: places[0].name, presetAddress: places[0].address }
                : {},
        };
        if (token) payload.token = token; else { payload.sessionId = form.sessionId; payload.email = meEmail; }

        ApiClient.post('api/cm/schedule/submit', payload).then((res: any) => {
            if (res.success) setDone(true);
            else { setError(res.message || 'Something went wrong. Please try again.'); setSubmitting(false); }
        }).catch(() => { setError('Something went wrong. Please try again.'); setSubmitting(false); });
    };

    const label = form?.label || 'ConnectMeet';

    if (loading) return <Center><p className="text-gray-500">Loading…</p></Center>;
    if (error && !form) return <Center><Card><h1 className="text-xl font-bold mb-2">Invalid link</h1><p className="text-gray-600">{error}</p></Card></Center>;
    if (done) return (
        <Center><Card>
            <div className="text-green-500 text-5xl mb-3">✓</div>
            <h1 className="text-2xl font-bold mb-2">Thanks!</h1>
            <p className="text-gray-600">Your {label} preferences were saved. We'll email you the meeting details once everyone has responded and we find a match.</p>
        </Card></Center>
    );

    return (
        <Center>
            <Card wide>
                <h1 className="text-2xl font-bold mb-1">Schedule your {label}</h1>
                {form?.groupName && <p className="text-sm text-gray-500 mb-4">{form.groupName}{form.eventName ? ` · ${form.eventName}` : ''}</p>}

                {/* Participants (pre-filled) */}
                <Section title="Your group">
                    <div className="space-y-1">
                        {form?.participants?.map((p: any, i: number) => (
                            <div key={i} className="flex justify-between text-sm">
                                <span>{p.name}</span>
                                <span className={p.status === 'submitted' ? 'text-green-600' : 'text-gray-400'}>{p.status === 'submitted' ? 'submitted' : 'waiting'}</span>
                            </div>
                        ))}
                    </div>
                </Section>

                {/* Who are you? Shown only when the participant is NOT already known.
                    With a per-member token (or a session link that resolves "me"), meLocked is set,
                    meEmail is auto-filled, and this picker is hidden so the user needn't choose. */}
                {!token && !meLocked && (
                    <Section title="Which participant are you?">
                        <select value={meEmail} onChange={e => setMeEmail(e.target.value)} className="w-full border rounded p-2 text-sm">
                            <option value="">Select your name…</option>
                            {form?.participants?.map((p: any, i: number) => (
                                <option key={i} value={p.email}>{p.name} ({p.email})</option>
                            ))}
                        </select>
                    </Section>
                )}

                {/* Mode (multi-select: pick either or both) */}
                <Section title="How would you like to meet? (pick one or both)">
                    <div className="flex flex-wrap gap-2">
                        {[{ v: 'in-person', l: 'In-person' }, { v: 'virtual', l: 'Virtual' }].map(o => (
                            <button key={o.v} type="button" onClick={() => toggleMode(o.v)}
                                className={`px-4 py-2 rounded border text-sm ${modeOn(o.v) ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700'}`}>
                                {modeOn(o.v) ? '✓ ' : ''}{o.l}
                            </button>
                        ))}
                    </div>
                </Section>

                {/* Activity */}
                <Section title="Activity">
                    <Radio options={ACTIVITIES.map(a => ({ v: a, l: a }))} value={activity} onChange={setActivity} />
                    {activity === 'Other' && (
                        <input value={activityOther} onChange={e => setActivityOther(e.target.value)} placeholder="Describe the activity" className="mt-2 w-full border rounded p-2 text-sm" />
                    )}
                </Section>

                {/* Location (in-person) — place picker (no free-form typing) */}
                {modeOn('in-person') && (
                    <Section title="Where would you like to meet?">
                        {myPlaces.length === 0 && othersPlaces.length === 0 ? (
                            <p className="text-sm text-gray-500">
                                You haven't saved any favorite places for {activity}. Add favorite places here:{' '}
                                <a href="/profile/personal-settings#favorite-places" target="_blank" rel="noreferrer" className="text-orange-500 underline font-semibold">{label} Preferences</a>
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {myPlaces.length > 0 && (
                                    <PlaceGroup title={`Your saved places for ${activity}`}
                                        places={myPlaces} isOn={placeOn} onToggle={togglePlace} />
                                )}
                                {othersPlaces.length > 0 && (
                                    <PlaceGroup title="Places others suggested"
                                        places={othersPlaces} isOn={placeOn} onToggle={togglePlace} />
                                )}
                                <p className="text-xs text-gray-400">Select one or more places you'd be happy to meet at.</p>
                            </div>
                        )}
                    </Section>
                )}

                {/* Video link (virtual, optional) */}
                {modeOn('virtual') && (
                    <Section title="Your video link (optional)">
                        <input value={videoLink} onChange={e => setVideoLink(e.target.value)} placeholder="https://zoom.us/j/… (we use the first link submitted)" className="w-full border rounded p-2 text-sm" />
                    </Section>
                )}

                {/* Availability, next 4 weeks */}
                <Section title="When are you available? (next 4 weeks)">
                    {/* Calendar free/busy overlay toggle */}
                    <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
                        <input type="checkbox" checked={useCalendar} disabled={calLoading} onChange={toggleUseCalendar} />
                        <span className="text-gray-700">{calLoading ? 'Reading your calendar…' : 'Use my calendar to fill in my availability'}</span>
                    </label>
                    {calNote && (
                        <p className="text-xs text-amber-600 mb-2">
                            {calNote}
                            {calShowConnect && (
                                <> <a href="/profile/personal-settings#calendar-connections" target="_blank" rel="noreferrer" className="text-orange-600 underline">{label} Preferences</a></>
                            )}
                        </p>
                    )}

                    {/* Clear / Select all */}
                    <div className="flex items-center gap-3 mb-2">
                        <button type="button" onClick={clearAll} className="text-xs text-gray-500 hover:text-gray-700 underline">Clear all</button>
                        <button type="button" onClick={selectAll} className="text-xs text-gray-500 hover:text-gray-700 underline">Select all</button>
                    </div>

                    {/* Calendar-style drag-select grid (when2meet / Schej look): days across the
                        top (date over weekday), hourly rows with the hour label sitting on the
                        gridline and a dashed half-hour divider. Horizontally scrollable; the time
                        gutter and day-header stay pinned. Click-and-drag (or touch-drag) to paint. */}
                    <div className="flex flex-col md:flex-row gap-4 items-start">
                    <div ref={gridRef} className={`flex-1 min-w-0 overflow-x-auto select-none ${dragging ? 'cursor-pointer' : ''}`} style={{ WebkitOverflowScrolling: 'touch' }}>
                        <div style={{ minWidth: 'max-content' }}>
                            {/* Day-header row (sticky top): date over weekday */}
                            <div className="flex sticky top-0 z-20 bg-white">
                                <div className="w-16 shrink-0 sticky left-0 z-30 bg-white" />
                                {days.map(day => {
                                    const d = new Date(day.y, day.m, day.d);
                                    const md = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                                    const wd = d.toLocaleDateString(undefined, { weekday: 'short' });
                                    return (
                                        <div key={day.key} className="w-20 shrink-0 px-1 pt-1 pb-2 text-center">
                                            <div className="text-[11px] text-gray-400 leading-tight">{md}</div>
                                            <div className="text-sm font-medium text-gray-700 leading-tight">{wd}</div>
                                        </div>
                                    );
                                })}
                            </div>
                            {/* Breathing room below the day headers so the first hour label isn't clipped by the header line. */}
                            <div className="h-3" />
                            {/* One row per hour */}
                            {TIME_BLOCKS.map((b, idx) => (
                                <div key={idx} className="flex">
                                    <div className="w-16 shrink-0 sticky left-0 z-10 bg-white pr-2 pt-1 text-right text-xs text-gray-400 flex items-start justify-end">
                                        <span className="whitespace-nowrap">{b.label}</span>
                                    </div>
                                    {days.map((day, di) => {
                                        const busy = blockIsBusy(day, idx);
                                        const hKey = `${day.key}:${idx}`;
                                        const oCount = heat.count[hKey] || 0;  // # available (selected) here
                                        const bCount = heat.busy[hKey] || 0;   // # calendar-busy here
                                        const oNames = heat.names[hKey];
                                        const groupSize = heat.size; // # of people who responded
                                        const everyoneFree = groupSize > 0 && oCount >= groupSize;
                                        // red = a member's CALENDAR is busy here; green = everyone who
                                        // responded is available (2+ people); yellow = some (but not all)
                                        // are available; blank = no availability / no busy info here.
                                        let cellStyle: React.CSSProperties | undefined;
                                        if (busy) {
                                            cellStyle = { backgroundImage: 'repeating-linear-gradient(45deg, #f3f4f6 0, #f3f4f6 4px, #e5e7eb 4px, #e5e7eb 8px)' };
                                        } else if (bCount > 0) {
                                            cellStyle = { backgroundColor: 'rgba(239,68,68,0.70)' };           // red: someone calendar-busy
                                        } else if (oCount > 0) {
                                            cellStyle = {
                                                backgroundColor: (everyoneFree && groupSize >= 2) ? 'rgba(34,197,94,0.80)'  // green: everyone free
                                                    : 'rgba(234,179,8,0.80)',                                                 // yellow: some available
                                            };
                                        }
                                        const tip = busy ? 'Busy on your calendar'
                                            : bCount > 0 ? 'Someone is busy — won\'t work for the whole group'
                                                : oCount === 0 ? 'No availability here yet'
                                                    : (everyoneFree && groupSize >= 2) ? `Everyone available: ${oNames ? oNames.join(', ') : ''}`
                                                        : `${oCount} available: ${oNames ? oNames.join(', ') : ''}`;
                                        return (
                                            <div key={day.key}
                                                data-daykey={day.key}
                                                data-block={idx}
                                                data-busy={busy ? '1' : '0'}
                                                title={tip}
                                                onMouseDown={() => startPaint(day.key, idx, busy)}
                                                onMouseEnter={() => enterPaint(day.key, idx, busy)}
                                                onTouchStart={() => startPaint(day.key, idx, busy)}
                                                style={cellStyle}
                                                className={`relative w-20 h-14 shrink-0 border-t border-r border-gray-200 ${di === 0 ? 'border-l' : ''} ${idx === TIME_BLOCKS.length - 1 ? 'border-b' : ''} ${busy ? 'cursor-not-allowed' : (cellStyle ? 'cursor-pointer hover:brightness-95' : 'bg-white hover:bg-orange-50 cursor-pointer')}`}>
                                                {!busy && !cellStyle && <div className="pointer-events-none absolute left-0 right-0 top-1/2 border-t border-dashed border-gray-200" />}
                                                {oCount > 0 && <span className="pointer-events-none absolute top-0.5 right-1 text-[9px] font-semibold text-gray-800">{oCount}</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>

                        {/* Schej-style participant panel: click names to filter the heatmap to those
                            people (toggle any number; none selected = whole group). */}
                        <div className="w-full md:w-44 shrink-0">
                            <div className="text-sm font-semibold text-gray-700 mb-1">Group ({(form?.memberSlots || []).length})</div>
                            <div className="text-sm text-gray-400 mb-2">Click a name to see just their times</div>
                            <ul className="space-y-1 mb-4">
                                {(form?.memberSlots || []).map((m: any, i: number) => {
                                    const active = activePeople.has(m.email);
                                    return (
                                        <li key={i}>
                                            <button
                                                type="button"
                                                onClick={() => togglePerson(m.email)}
                                                className={`w-full flex items-center gap-2 text-sm px-2 py-1 rounded transition-colors ${active ? 'bg-orange-500 text-white' : 'hover:bg-orange-50 text-gray-600'}`}
                                            >
                                                <FaUser className={`shrink-0 text-sm ${active ? 'text-white' : 'text-gray-400'}`} />
                                                <span className={`truncate ${m.isMe && !active ? 'font-semibold text-gray-800' : ''}`}>{m.name}{m.isMe ? ' (you)' : ''}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                            {activePeople.size > 0 && (
                                <button type="button" onClick={() => setActivePeople(new Set())} className="text-sm text-orange-500 hover:underline mb-3">Show everyone</button>
                            )}
                            <div className="text-sm text-gray-500">
                                <div className="mb-1 font-medium text-gray-600">{activePeople.size ? 'Selected people free' : 'Group availability'}</div>
                                <div className="flex flex-col gap-1">
                                    <span className="flex items-center gap-1"><span className="w-4 h-3 inline-block rounded-sm" style={{ background: 'rgba(34,197,94,0.80)' }} /><span className="text-sm text-gray-400">Everyone who submitted is available</span></span>
                                    <span className="flex items-center gap-1"><span className="w-4 h-3 inline-block rounded-sm" style={{ background: 'rgba(234,179,8,0.80)' }} /><span className="text-sm text-gray-400">Only some are available (number shown)</span></span>
                                    <span className="flex items-center gap-1"><span className="w-4 h-3 inline-block rounded-sm" style={{ background: 'rgba(239,68,68,0.70)' }} /><span className="text-sm text-gray-400">Someone is busy</span></span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{useCalendar ? 'Hatched cells are busy on your calendar. Green = everyone who submitted is available, yellow = only some are available (number shown), red = someone is busy. Click and drag across the cells you\'re free.' : "Green = everyone who submitted is available, yellow = only some are available (number shown), red = someone is busy. Click and drag across the cells you're free."}</p>
                </Section>

                {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
                <button onClick={submit} disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded">
                    {submitting ? 'Submitting…' : `Submit my ${label} preferences`}
                </button>
                <p className="text-xs text-gray-500 text-center mt-4">
                    You can update your {label} preferences anytime.{' '}
                    <a href="/profile/personal-settings#cm-preferences" className="text-orange-500 hover:underline">Change my {label} preferences</a>
                </p>
            </Card>
        </Center>
    );
};

// ── tiny presentational helpers (kept local to this public page) ──
const Center: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-10 px-4">{children}</div>
);
const Card: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
    <div className={`bg-white p-6 md:p-8 rounded-lg shadow-md w-full ${wide ? 'max-w-2xl' : 'max-w-md text-center'}`}>{children}</div>
);
const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-5"><h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>{children}</div>
);
// A selectable group of places (name + address). Multi-select: clicking toggles a place.
const PlaceGroup: React.FC<{
    title: string;
    places: { name: string; address: string }[];
    isOn: (p: { name: string; address: string }) => boolean;
    onToggle: (p: { name: string; address: string }) => void;
}> = ({ title, places, isOn, onToggle }) => (
    <div>
        <div className="text-xs font-medium text-gray-500 mb-1">{title}</div>
        <div className="flex flex-col gap-2">
            {places.map((pl, i) => {
                const on = isOn(pl);
                return (
                    <button key={`${pl.name}-${i}`} type="button" onClick={() => onToggle(pl)}
                        className={`text-left px-3 py-2 rounded border text-sm ${on ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700 border-gray-200 hover:bg-orange-50'}`}>
                        <span className="font-medium">{on ? '✓ ' : ''}{pl.name}</span>
                        {pl.address && <span className={`block text-xs ${on ? 'text-orange-100' : 'text-gray-400'}`}>{pl.address}</span>}
                    </button>
                );
            })}
        </div>
    </div>
);
const Radio: React.FC<{ options: { v: string; l: string }[]; value: string; onChange: (v: string) => void }> = ({ options, value, onChange }) => (
    <div className="flex flex-wrap gap-2">
        {options.map(o => (
            <button key={o.v} type="button" onClick={() => onChange(o.v)}
                className={`px-4 py-2 rounded border text-sm ${value === o.v ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-700'}`}>{o.l}</button>
        ))}
    </div>
);

export default CmSchedule;
